import { ValueType } from 'react-native-dropdown-picker';
import { FormChildProp } from '@/components/Form/types';
import React, { useEffect, useState } from 'react';
import { Icon, Text, useTheme } from 'react-native-paper';
import { Pressable, StyleProp, StyleSheet, TextStyle, View, ViewStyle } from 'react-native';
import { useWatch } from 'react-hook-form';
import { Border, Color, Padding } from '@/GlobalStyles';
import { useTranslation } from 'react-i18next';
import HelpButton from '@/components/HelpButton';

const IconListInput = React.forwardRef(
  <T extends ValueType>(
    props: {
      data?: {
        value: T;
        label: string;
        icon?: string;
        inactiveIcon?: string;
        activeColor?: string;
        inactiveColor?: string;
      }[];
      labelStyle?: StyleProp<TextStyle>;
      containerStyle?: StyleProp<ViewStyle>;
      withMargin?: boolean;
      withBottomBorder?: boolean;
    } & FormChildProp & {
        defaultValue?: T | T[] | null;
        value?: T | T[] | null;
      },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref
  ): React.ReactElement => {
    const {
      defaultValue,
      onChangeValue,
      error,
      value,
      name,
      label,
      data,
      withMargin,
      labelStyle,
      containerStyle,
      withBottomBorder = false,
      withHelpButton = false,
      helpFieldName,
    } = props;
    const theme = useTheme();

    const [valueSet, setValueSet] = useState<T | T[] | null | undefined>(value || defaultValue);

    const currentValue = useWatch({ name: name, defaultValue });

    const { t } = useTranslation();

    useEffect(() => {
      if (onChangeValue && valueSet !== value) {
        onChangeValue(
          valueSet !== null && valueSet !== undefined
            ? valueSet
            : value !== null && value !== undefined
              ? value
              : defaultValue || undefined
        );
      }
    }, [valueSet]);

    useEffect(() => {
      if (valueSet !== currentValue) {
        setValueSet(currentValue);
      }
    }, [currentValue]);

    return (
      <View
        style={[
          withMargin
            ? {
                marginBottom: 10,
              }
            : null,
          containerStyle,
        ]}
      >
        <View
          style={[
            styles.container,
            withMargin
              ? {
                  marginBottom: 10,
                }
              : null,
          ]}
        >
          {label ? (
            withHelpButton && (helpFieldName || name) ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                <Text
                  variant={'labelMedium'}
                  style={[styles.label, { marginBottom: 0 }, labelStyle]}
                >
                  {t(label)}
                </Text>
                <HelpButton
                  fieldName={helpFieldName || name || ''}
                  size={16}
                  style={{ marginLeft: 4 }}
                />
              </View>
            ) : (
              <Text variant={'labelMedium'} style={[styles.label, labelStyle]}>
                {t(label)}
              </Text>
            )
          ) : null}
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-around',
            }}
          >
            {data
              ? data.map(
                  ({
                    value,
                    label,
                    icon,
                    inactiveIcon,
                    activeColor = Color.MainBleu,
                    inactiveColor = Color.VertElectrique,
                  }) => (
                    <Pressable
                      key={name + '_' + value}
                      onPress={() => {
                        setValueSet(value);
                      }}
                      style={{
                        flex: 1,
                        alignItems: 'center',
                        /*marginHorizontal: 5,*/
                        borderWidth: 1,
                        borderColor: value === valueSet ? activeColor : 'transparent',
                        borderRadius: Border.br_3xs,
                        paddingVertical: 5,
                        paddingHorizontal: 5,
                      }}
                    >
                      <Icon
                        size={30}
                        source={
                          value === valueSet
                            ? icon || 'check-circle'
                            : inactiveIcon || icon || 'checkbox-blank-circle-outline'
                        }
                        color={value === valueSet ? activeColor : inactiveColor}
                      />
                      <Text
                        variant={'labelMedium'}
                        style={{ textAlign: 'center' }}
                        lineBreakMode={'clip'}
                        ellipsizeMode={'head'}
                      >
                        {t(label)}
                      </Text>
                    </Pressable>
                  )
                )
              : null}
          </View>
          {error?.message ? (
            <Text
              variant={'labelMedium'}
              style={{
                color: theme.colors.error,
                marginLeft: 18,
                bottom: -18,
                flex: 1,
                position: 'absolute',
              }}
            >
              {props.error?.message}
            </Text>
          ) : null}
          {withBottomBorder ? (
            <View style={{ marginTop: 10, height: 1, backgroundColor: Color.MainBleu }} />
          ) : null}
        </View>
      </View>
    );
  }
);

export default IconListInput;

const styles = StyleSheet.create({
  container: {
    flexShrink: 0,
    flexGrow: 1,
    flexDirection: 'column',
  },
  label: {
    paddingBottom: 5,
  },
});
